// Cross-app URL helpers for hive-ui. Env-detection mirrors auth/src/App.vue's
// getHiveUrl/getApiUrl pattern (hostname suffix → fully-qualified origin).

function hostname(): string {
    if (typeof window === 'undefined') return '';
    return window.location.hostname;
}

export function pricingUrl(): string {
    const h = hostname();
    if (h.includes('hive-dev.privacybee.com')) return 'https://dev.privacybee.com/pricing';
    if (h.includes('hive.privacybee.com')) return 'https://privacybee.com/pricing';
    return 'https://privacybee.test/pricing';
}

export function checkoutOrigin(): string {
    const h = hostname();
    if (h.includes('hive-dev.privacybee.com')) return 'https://checkout-dev.privacybee.com';
    if (h.includes('hive.privacybee.com')) return 'https://checkout.privacybee.com';
    return 'https://checkout.privacybee.test';
}

/** The term as checkout's links spell it. The link says `yearly`; the page says "Annual". */
export type CheckoutTerm = 'monthly' | 'yearly' | 'two_year' | 'three_year';

// Builds the cross-app upgrade URL. `returnTo` is the hive-relative path the user
// should return to if they dismiss the modal in checkout without picking a plan.
//
// `feature`, `plan` and `term` are HINTS, not instructions: checkout resolves the feature
// against its own gate registry and re-quotes whatever it settles on, so a value this app got
// wrong costs the member a different preselection, never a different charge. Omitted values add
// no parameter at all, which is what keeps every existing two-argument caller's URL unchanged.
export function checkoutUpgradeUrl(
    licenseId: number,
    returnTo: string,
    opts: { feature?: string; plan?: string; term?: CheckoutTerm } = {},
): string {
    const params = new URLSearchParams({
        mode: 'upgrade',
        license_id: String(licenseId),
        return_to: returnTo,
    });
    if (opts.feature) params.set('feature', opts.feature);
    if (opts.plan) params.set('plan', opts.plan);
    if (opts.term) params.set('term', opts.term);
    return `${checkoutOrigin()}/?${params.toString()}`;
}

// Builds the cross-app new-purchase URL. `select=1` tells checkout to open the select-plan
// modal on load (instead of the marketing /pricing page). `returnTo` is the hive-relative
// path to return to if the buyer dismisses the modal without choosing a plan. An optional
// promo code is carried through and auto-applied in checkout. `feature` leads the plan selector
// with the locked feature the buyer came for — the plan that carries it is flagged and its
// comparison row marked.
export function checkoutPurchaseUrl(returnTo: string, opts: { promo?: string; feature?: string } = {}): string {
    const params = new URLSearchParams({ select: '1', return_to: returnTo });
    if (opts.promo) params.set('promo', opts.promo.toUpperCase());
    if (opts.feature) params.set('feature', opts.feature);
    return `${checkoutOrigin()}/?${params.toString()}`;
}

// Builds the cross-app purchase URL for buying a license to assign to a family member. Carries
// the contact id + name so checkout auto-assigns the new license on completion and shows
// "purchasing for {name}" wording; `select=1` opens the plan picker. `returnTo` is the hive path
// to return to (typically /family).
export function checkoutAssignUrl(contact: { id: number; name?: string }, returnTo: string): string {
    const params = new URLSearchParams({
        select: '1',
        assign_to_contact_id: String(contact.id),
        assign_to_name: contact.name ?? '',
        return_to: returnTo,
    });
    return `${checkoutOrigin()}/?${params.toString()}`;
}

// Builds the cross-app URL for buying COMPANY seats. `companyId` tells checkout whose
// seats these are — a hint only: checkout re-checks the buyer's financial-write
// permission on that company server-side before it prices or charges anything.
// `returnTo` is the hive-relative path to come back to once the order lands.
//
// `plan` is the business tier slug the buyer came to add more of, and it lands them on
// that plan's order summary instead of the plan chooser — a hint like the others:
// checkout ignores a slug its own catalogue does not carry (a partner SKU, a retired
// tier), and re-quotes whatever the buyer confirms. Omitted, it adds no parameter, so a
// caller that has no one tier in mind still opens the chooser.
export function checkoutBusinessUrl(companyId: number, returnTo: string, opts: { plan?: string } = {}): string {
    const params = new URLSearchParams({ company: String(companyId), return_to: returnTo });
    if (opts.plan) params.set('plan', opts.plan);
    return `${checkoutOrigin()}/business?${params.toString()}`;
}
